import {useEffect, useMemo, useState} from 'react';
import {Grid, MenuItem} from '@mui/material';
import {AutocompleteField, ExpansionPanel, InputField, TaxRepBrField, TaxRepTelephoneField} from '@/components/common';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';
import type {CompanyAnnual} from '@/services/accountService/companyAnnualService';
import {useCertificateStore} from '@/store/admin/certificateStore';
import {TaxRepService, type TaxRepVO} from '@/services/manageService/taxRepService';
import {defaultTaxRepPhone, hasTaxRepPhoneLocal, normalizeTaxRepPhone} from '@/utils/taxRepPhone';

/** Unified Tax Rep Autocomplete option (Certificate ∪ saved Tax Rep). */
type TaxRepOption = {
    key: string;
    /** Display label (may include disambiguation suffix). */
    label: string;
    /** Raw Auditor / Tax Rep name without suffix. */
    name: string;
    source: 'certificate' | 'taxRep';
    certificateId?: number;
    taxRepId?: number;
    telephone: string;
    herebyAuthorize: string;
    address: string;
    brNumber: string;
};

type TaxRepDetailLocal = {
    telephone: string;
    herebyAuthorize: string;
    address: string;
    brNumber: string;
};

type Props = {
    annualId: number;
    onChange?: (patch: Partial<CompanyAnnual>) => void;
    disableAccordion?: boolean;
    externalErrors?: {
        auditCertificateId?: boolean;
        taxRep?: boolean;
    };
};

const emptyDetail: TaxRepDetailLocal = {
    telephone: '',
    herebyAuthorize: '',
    address: '',
    brNumber: '',
};

/**
 * Auditor panel + Tax Rep Autocomplete (Certificate by Auditor Name ∪ admin_tax_rep).
 * Select list item → display-only detail; freeSolo type → editable required detail (saved on annual submit).
 */
export default function EpAuditor({
                                      annualId,
                                      onChange,
                                      disableAccordion = false,
                                      externalErrors
                                  }: Props) {
    const getAuditorInfo = useCompanyAnnualStore(s => s.getAuditorInfo);
    const getTaxRepInfo = useCompanyAnnualStore(s => s.getTaxRepInfo);
    const storeAnnual = useCompanyAnnualStore(s => s.byAnnualId[Number(annualId)]);
    const loadAllCert = useCertificateStore(s => s.loadAllCert);
    const certificateOptions = useCertificateStore(s => s.all);
    const [local, setLocal] = useState<{
        auditCertificateId?: number;
        irdLetterSigner: string
    }>({auditCertificateId: undefined, irdLetterSigner: ''});
    const [selectedCertificateId, setSelectedCertificateId] = useState<string>('');
    const [taxRepList, setTaxRepList] = useState<TaxRepVO[]>([]);
    const [taxRepInput, setTaxRepInput] = useState<string>('');
    /** selected = from Autocomplete list; manual = freeSolo; empty = nothing chosen */
    const [taxRepMode, setTaxRepMode] = useState<'empty' | 'selected' | 'manual'>('empty');
    const [taxRepDetail, setTaxRepDetail] = useState<TaxRepDetailLocal>(emptyDetail);

    const selectedCertificate = useMemo(() => {
        const id = parseInt(selectedCertificateId, 10);
        if (isNaN(id)) return null;
        return certificateOptions.find(x => x.id === id) || null;
    }, [selectedCertificateId, certificateOptions]);

    const taxRepOptions = useMemo((): TaxRepOption[] => {
        // Certificate ∪ admin_tax_rep; exclude soft-deleted certificates (is_del=1)
        const certOpts: TaxRepOption[] = (certificateOptions || [])
            .filter(c => Number(c.isDel ?? 0) !== 1)
            .filter(c => (c.auditorEnglishName || '').trim())
            .map(c => {
                const name = (c.auditorEnglishName || '').trim();
                return {
                    key: `cert:${c.id}`,
                    label: name,
                    name,
                    source: 'certificate' as const,
                    certificateId: c.id,
                    telephone: c.auditorTelBankConfNumber || '',
                    herebyAuthorize: name ? `${name}, CPA` : '',
                    address: c.auditorAddress || '',
                    brNumber: c.auditorBrNumber || '',
                };
            });
        const repOpts: TaxRepOption[] = (taxRepList || [])
            .filter(r => (r.taxRepName || '').trim())
            .map(r => {
                const name = (r.taxRepName || '').trim();
                return {
                    key: `rep:${r.id}`,
                    label: name,
                    name,
                    source: 'taxRep' as const,
                    taxRepId: r.id,
                    telephone: r.telephone || '',
                    // 仅用库中快照；空则 PTR 对手输/自建 Tax Rep 不加 CPA
                    herebyAuthorize: r.herebyAuthorize || '',
                    address: r.address || '',
                    brNumber: r.brNumber || '',
                };
            });
        // Disambiguate duplicate display labels across the two sources
        const labelCount = new Map<string, number>();
        [...certOpts, ...repOpts].forEach(o => {
            labelCount.set(o.label, (labelCount.get(o.label) || 0) + 1);
        });
        return [...certOpts, ...repOpts].map(o => {
            if ((labelCount.get(o.label) || 0) <= 1) return o;
            const suffix = o.source === 'certificate' ? 'Certificate' : 'Tax Rep';
            return {...o, label: `${o.name} (${suffix})`};
        });
    }, [certificateOptions, taxRepList]);

    const taxRepLabelOptions = useMemo(() => taxRepOptions.map(o => o.label), [taxRepOptions]);
    const taxRepByLabel = useMemo(() => {
        const m = new Map<string, TaxRepOption>();
        taxRepOptions.forEach(o => m.set(o.label, o));
        return m;
    }, [taxRepOptions]);

    useEffect(() => {
        let mounted = true;
        (async () => {
            const [info, taxRepInfo, reps] = await Promise.all([
                getAuditorInfo(annualId),
                getTaxRepInfo(annualId),
                TaxRepService.querySelectionList().catch(() => [] as TaxRepVO[]),
                // Force refresh so soft-deleted certificates leave Autocomplete cache
                loadAllCert({force: true}),
            ]);
            if (!mounted) return;
            setLocal({auditCertificateId: info.auditCertificateId, irdLetterSigner: info.irdLetterSigner || ''});
            setSelectedCertificateId(info.auditCertificateId ? String(info.auditCertificateId) : '');
            setTaxRepList(Array.isArray(reps) ? reps : []);

            // Restore Tax Rep: prefer taxRepId over certificateId (MP used to leave both FKs).
            const certId = taxRepInfo.taxRepCertificateId;
            const repId = taxRepInfo.taxRepId;
            const repList = Array.isArray(reps) ? reps : [];
            if (repId) {
                const fromMaster = repList.find(r => Number(r.id) === Number(repId));
                const name = (fromMaster?.taxRepName || taxRepInfo.taxRepName || '').trim();
                setTaxRepInput(name);
                setTaxRepMode('selected');
                setTaxRepDetail({
                    telephone: normalizeTaxRepPhone(fromMaster?.telephone || taxRepInfo.taxRepTel || ''),
                    herebyAuthorize: name,
                    address: fromMaster?.address || taxRepInfo.taxRepAddress || '',
                    brNumber: fromMaster?.brNumber || taxRepInfo.taxRepBrNumber || '',
                });
            } else if (certId) {
                const cert = useCertificateStore.getState().all.find(c => c.id === certId);
                const name = (cert?.auditorEnglishName || taxRepInfo.taxRepName || '').trim();
                setTaxRepInput(name);
                setTaxRepMode('selected');
                setTaxRepDetail({
                    telephone: normalizeTaxRepPhone(cert?.auditorTelBankConfNumber || ''),
                    herebyAuthorize: name ? `${name}, CPA` : '',
                    address: cert?.auditorAddress || '',
                    brNumber: cert?.auditorBrNumber || '',
                });
            } else if (Number(taxRepInfo.taxRepSameAsAuditor) === 0) {
                const name = (taxRepInfo.taxRepName || '').trim();
                setTaxRepInput(name);
                setTaxRepMode(name ? 'manual' : 'empty');
                setTaxRepDetail({
                    telephone: normalizeTaxRepPhone(taxRepInfo.taxRepTel || ''),
                    herebyAuthorize: taxRepInfo.taxRepHerebyAuthorize || '',
                    address: taxRepInfo.taxRepAddress || '',
                    brNumber: taxRepInfo.taxRepBrNumber || '',
                });
            } else {
                setTaxRepInput('');
                setTaxRepMode('empty');
                setTaxRepDetail(emptyDetail);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [annualId, getAuditorInfo, getTaxRepInfo, loadAllCert]);

    useEffect(() => {
        if (!storeAnnual) return;
        setLocal(prev => {
            const nextCertId = storeAnnual.auditCertificateId ?? prev.auditCertificateId;
            const nextSigner = storeAnnual.irdLetterSigner ?? prev.irdLetterSigner;
            if (nextCertId === prev.auditCertificateId && nextSigner === prev.irdLetterSigner) return prev;
            return {auditCertificateId: nextCertId, irdLetterSigner: nextSigner || ''};
        });
        if (storeAnnual.auditCertificateId) {
            setSelectedCertificateId(prev => {
                const next = String(storeAnnual.auditCertificateId);
                return prev === next ? prev : next;
            });
        }
    }, [storeAnnual]);

    const emit = (patch: Partial<typeof local>) => {
        const next = {...local, ...patch};
        setLocal(next);
        onChange?.(patch as Partial<CompanyAnnual>);
    };

    const emitTaxRepPatch = (patch: Partial<CompanyAnnual>) => {
        onChange?.(patch);
    };

    const applySelectedOption = (opt: TaxRepOption) => {
        setTaxRepInput(opt.label);
        setTaxRepMode('selected');
        setTaxRepDetail({
            telephone: normalizeTaxRepPhone(opt.telephone),
            herebyAuthorize: opt.herebyAuthorize,
            address: opt.address,
            brNumber: opt.brNumber,
        });
        if (opt.source === 'certificate') {
            // List selection — do not INSERT admin_tax_rep
            emitTaxRepPatch({
                taxRepSameAsAuditor: 1,
                taxRepCertificateId: opt.certificateId ?? null,
                taxRepId: null,
                taxRepName: null,
                taxRepTel: null,
                taxRepHerebyAuthorize: null,
                taxRepAddress: null,
                taxRepBrNumber: null,
            });
        } else {
            emitTaxRepPatch({
                taxRepSameAsAuditor: 0,
                taxRepCertificateId: null,
                taxRepId: opt.taxRepId ?? null,
                taxRepName: opt.name,
                taxRepTel: opt.telephone,
                // 与展示名对齐（UI 无 herebyAuthorize 编辑）；避免主档脏快照进 Part 6
                taxRepHerebyAuthorize: opt.name,
                taxRepAddress: opt.address,
                taxRepBrNumber: opt.brNumber,
            });
        }
    };

    const applyManualName = (name: string) => {
        const trimmed = (name || '').trim();
        setTaxRepInput(trimmed);
        if (!trimmed) {
            setTaxRepMode('empty');
            setTaxRepDetail(emptyDetail);
            emitTaxRepPatch({
                taxRepSameAsAuditor: 1,
                taxRepCertificateId: null,
                taxRepId: null,
                taxRepName: null,
                taxRepTel: null,
                taxRepHerebyAuthorize: null,
                taxRepAddress: null,
                taxRepBrNumber: null,
            });
            return;
        }
        setTaxRepMode('manual');
        setTaxRepDetail({
            ...emptyDetail,
            telephone: defaultTaxRepPhone(),
        });
        emitTaxRepPatch({
            taxRepSameAsAuditor: 0,
            taxRepCertificateId: null,
            taxRepId: null,
            taxRepName: trimmed,
            taxRepTel: defaultTaxRepPhone(),
            taxRepHerebyAuthorize: '',
            taxRepAddress: '',
            taxRepBrNumber: '',
        });
    };

    const onTaxRepDetailChange = (patch: Partial<TaxRepDetailLocal>) => {
        const next = {...taxRepDetail, ...patch};
        setTaxRepDetail(next);
        emitTaxRepPatch({
            taxRepTel: next.telephone,
            taxRepHerebyAuthorize: next.herebyAuthorize,
            taxRepAddress: next.address,
            taxRepBrNumber: next.brNumber,
        });
    };

    const certificateSelectValue =
        certificateOptions.length === 0 && selectedCertificateId
            ? ''
            : selectedCertificateId;

    const showTaxRepDetail = taxRepMode === 'selected' || taxRepMode === 'manual';
    const taxRepDisplay = taxRepMode === 'selected';

    return (
        <ExpansionPanel
            title="Auditor (審計師)"
            expanded={disableAccordion ? true : undefined}
            onChange={disableAccordion ? () => {
            } : undefined}
        >
            <Grid container spacing={0.5}>
                <Grid size={12 as any}>
                    <InputField
                        select
                        fullWidth
                        variant="outlined"
                        label="Audit Certificate"
                        value={certificateSelectValue}
                        onChange={(e) => {
                            const str = e.target.value as string;
                            setSelectedCertificateId((str === '' ? '' : String(Number(str))) as any);
                            emit({auditCertificateId: (str === '' ? 0 : Number(str)) as any});
                        }}
                        error={externalErrors?.auditCertificateId}
                        helperText={externalErrors?.auditCertificateId ? 'Audit Certificate is required' : undefined}
                    >
                        <MenuItem dense value="">Select</MenuItem>
                        {certificateOptions
                            .filter(co => Number(co.isDel ?? 0) !== 1)
                            .map(co => (
                            <MenuItem dense key={co.id}
                                      value={co.id}>{co.auditorEnglishName + ' --- ' + co.auditorAddress || `#${co.id}`}</MenuItem>
                        ))}
                    </InputField>
                </Grid>
                {selectedCertificate && (
                    <>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Auditor Name"
                                        value={selectedCertificate.auditorEnglishName || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Sign Name"
                                        value={selectedCertificate.auditorSignName || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Accounting Firm Name"
                                        value={selectedCertificate.accountingFirmName || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Accounting Firm Chinese Name"
                                        value={selectedCertificate.accountingFirmChineseName || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="PC Number"
                                        value={selectedCertificate.pcNumber || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" display label="Auditor Address"
                                        value={selectedCertificate.auditorAddress || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField fullWidth variant="outlined" display label="Auditor Chinese Address"
                                        value={selectedCertificate.auditorChineseAddress || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Fax Number"
                                        value={selectedCertificate.auditorFaxNumber || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Tel (Rep)"
                                        value={selectedCertificate.auditorTelRepNumber || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="Tel (Bank Conf)"
                                        value={selectedCertificate.auditorTelBankConfNumber || ''} onChange={() => {
                            }}/>
                        </Grid>
                        <Grid size={6 as any}>
                            <InputField fullWidth variant="outlined" display label="BR Number"
                                        value={selectedCertificate.auditorBrNumber || ''} onChange={() => {
                            }}/>
                        </Grid>
                    </>
                )}
                <Grid size={6 as any}>
                    <InputField fullWidth variant="outlined" label="IRD Letter signer (稅局信的簽署人名稱)"
                                value={local.irdLetterSigner}
                                onChange={(e) => emit({irdLetterSigner: e.target.value})}/>
                </Grid>

                <Grid size={12 as any}>
                    <AutocompleteField
                        fullWidth
                        forcePopupIcon
                        freeSolo
                        commitOnBlur
                        align="left"
                        label="Tax Rep"
                        options={taxRepLabelOptions}
                        value={taxRepInput || null}
                        onChange={(v) => {
                            const raw = (v || '').trim();
                            if (!raw) {
                                applyManualName('');
                                return;
                            }
                            const opt = taxRepByLabel.get(raw);
                            if (opt) {
                                applySelectedOption(opt);
                            } else {
                                applyManualName(raw);
                            }
                        }}
                    />
                </Grid>

                {showTaxRepDetail && (
                    <>
                        <Grid size={6 as any}>
                            <TaxRepTelephoneField
                                fullWidth
                                display={taxRepDisplay}
                                label="Telephone Number"
                                value={taxRepDetail.telephone}
                                error={!!externalErrors?.taxRep && !hasTaxRepPhoneLocal(taxRepDetail.telephone)}
                                onChange={(next) => onTaxRepDetailChange({telephone: next})}
                            />
                        </Grid>
                        <Grid size={6 as any}>
                            <TaxRepBrField
                                fullWidth
                                display={taxRepDisplay}
                                value={taxRepDetail.brNumber}
                                error={!!externalErrors?.taxRep && !String(taxRepDetail.brNumber || '').trim()}
                                onChange={(next) => onTaxRepDetailChange({brNumber: next})}
                            />
                        </Grid>
                        <Grid size={12 as any}>
                            <InputField
                                fullWidth
                                variant="outlined"
                                display={taxRepDisplay}
                                label="of (Address)"
                                value={taxRepDetail.address}
                                error={!!externalErrors?.taxRep && !taxRepDetail.address.trim()}
                                onChange={(e) => onTaxRepDetailChange({address: e.target.value})}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </ExpansionPanel>
    );
}
